import { eachDayOfInterval, isWeekend, parseISO, isValid, addDays, getDay } from "date-fns";

function parseDateOnly(input: Date | string): Date {
  if (input instanceof Date) {
    return new Date(input.getFullYear(), input.getMonth(), input.getDate());
  }

  const match = input.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (match) {
    return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  }

  const parsed = parseISO(input);
  return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
}

function formatDateOnly(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/**
 * Count working days between start and end (inclusive),
 * excluding weekends and public holidays.
 *
 * @param startDate  ISO string or Date
 * @param endDate    ISO string or Date
 * @param holidayDates  Array of ISO date strings ('YYYY-MM-DD') to exclude
 */
export function countWorkingDays(
  startDate: Date | string,
  endDate: Date | string,
  holidayDates: string[] = [],
): number {
  const start = parseDateOnly(startDate);
  const end = parseDateOnly(endDate);

  if (!isValid(start) || !isValid(end) || end < start) return 0;

  const holidaySet = new Set(holidayDates);
  const days = eachDayOfInterval({ start, end });

  return days.filter((d) => {
    if (isWeekend(d)) return false;
    return !holidaySet.has(formatDateOnly(d));
  }).length;
}

/**
 * Given a list of {date, next_monday_off} rows, return all holiday dates
 * (original holiday + observed Monday for weekend holidays where next_monday_off=true).
 */
export function expandObservedMondays(
  rows: { date: string; next_monday_off?: boolean }[],
): string[] {
  const dates: string[] = [];
  for (const row of rows) {
    dates.push(row.date);
    if (row.next_monday_off) {
      const d = parseDateOnly(row.date);
      const dow = getDay(d); // 0=Sun, 6=Sat
      if (dow === 6) dates.push(formatDateOnly(addDays(d, 2)));
      else if (dow === 0) dates.push(formatDateOnly(addDays(d, 1)));
    }
  }
  return dates;
}

/**
 * Format a date string as a human-readable label (e.g. "18 Feb 2026")
 */
export function formatLeaveDate(dateStr: string): string {
  const d = parseISO(dateStr);
  if (!isValid(d)) return dateStr;
  return d.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}
